import React, { ReactNode } from 'react';
import { getPaginationItems } from './table_utils';
import { flexRender } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
// import { Skeleton } from 'antd';

interface DeepechoTableProps {
  table: any;
  totalCount: any;
  isLoading: boolean;
  pageSize: number;
  columnsLength: number;
  expandedRows?: Record<string, boolean>;
  renderSubComponent?: (row: any) => ReactNode;
}

const Table: React.FC<DeepechoTableProps> = ({
  table,
  totalCount,
  isLoading,
  pageSize,
  columnsLength,
  expandedRows,
  renderSubComponent,
}) => {
  const { t } = useTranslation('Common');
  const getCommonPinningStyles = (column) => {
    const isPinned = column.getIsPinned();
    const isLastLeftPinnedColumn =
      isPinned === 'left' && column.getIsLastColumn('left');
    const isFirstRightPinnedColumn =
      isPinned === 'right' && column.getIsFirstColumn('right');
    const styles: React.CSSProperties = {
      boxShadow: isLastLeftPinnedColumn
        ? '-4px 0 4px -4px gray inset'
        : isFirstRightPinnedColumn
          ? '4px 0 4px -4px gray inset'
          : undefined,
      left: isPinned === 'left' ? `${column.getStart('left')}px` : undefined,
      right: isPinned === 'right' ? `${column.getAfter('right')}px` : undefined,
      opacity: isPinned ? 0.95 : 1,
      position: isPinned ? 'sticky' : 'relative',
      width: column?.id == 'select' ? 50 : column.getSize(),
      // zIndex: isPinned ? 1 : 0,
    };
    return styles as React.CSSProperties;
  };
  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-hidden border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm bg-white dark:bg-gray-800">
        <div className="h-full overflow-auto">
          <table id="user-table" className="w-full min-w-[800px]">
            <thead className="bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-10">
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    return (
                      <th
                        className={`py-3.5 px-4 text-left text-sm font-semibold text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-900 ${
                          header.column.getIsPinned() ? 'sticky-td' : ''
                        }`}
                        key={header.id}
                        colSpan={header.colSpan}
                        style={{ ...getCommonPinningStyles(header.column) }}
                      >
                        {header.isPlaceholder ? null : (
                          <>
                            {flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                          </>
                        )}
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700 bg-white dark:bg-gray-800">
              {isLoading ? (
                Array.from({ length: pageSize }).map((_, rowIndex) => {
                  return (
                    <tr key={rowIndex}>
                      {Array.from({ length: columnsLength }).map((_, colIndex) => {
                        return (
                          <td key={colIndex}>
                            <div className="p-4">
                              <div className="h-4 w-full rounded bg-gray-200 dark:bg-gray-600 animate-pulse" />
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })
              ) : (
                <>
                  {totalCount === 0 ? (
                    <tr>
                      <td
                        colSpan={table.getHeaderGroups()[0]?.headers.length}
                        className="p-8 text-center"
                      >
                        <div className="flex flex-col items-center justify-center text-gray-500">
                          <i className="ri-file-line mb-2 text-4xl text-gray-400"></i>
                          <span className="text-sm font-medium">{t('noDataAvailable')}</span>
                          <span className="text-xs text-gray-400 mt-1">{t('noDataAdjustFilters')}</span>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    <>
                      {table.getRowModel().rows.map((row) => {
                        return (
                          <React.Fragment key={row.id}>
                            <tr
                              id={`table-row-${row.id}`}
                              className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-150 text-gray-900 dark:text-gray-200"
                            >
                              {row.getVisibleCells().map((cell) => {
                                return (
                                  <td
                                    key={cell.id}
                                    className={`py-3.5 px-4 ${
                                      cell.column.getIsPinned() ? 'sticky-td' : ''
                                    }`}
                                    style={{ ...getCommonPinningStyles(cell.column) }}
                                  >
                                    {flexRender(
                                      cell.column.columnDef.cell,
                                      cell.getContext()
                                    )}
                                  </td>
                                );
                              })}
                            </tr>
                            {expandedRows && expandedRows[row.id] && renderSubComponent && (
                              <tr>
                                <td colSpan={table.getHeaderGroups()[0]?.headers.length} className="p-0">
                                  {renderSubComponent(row.original)}
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </>
                  )}
                </>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <div className="flex w-full items-center justify-between px-2 mt-4 flex-shrink-0">
        <div className="text-sm text-gray-600 dark:text-gray-400">
          Showing{' '}
          <span className="font-medium text-gray-900 dark:text-gray-200">
            {table.getRowModel().rows.length.toLocaleString()}
          </span>{' '}
          of <span className="font-medium text-gray-900 dark:text-gray-200">{totalCount}</span> results
        </div>
        <div id="pagination" className="flex items-center gap-1">
          <button
            className={`h-8 w-8 rounded-md border transition-colors ${
              !table.getCanPreviousPage()
                ? 'border-gray-200 dark:border-gray-700 text-gray-400 dark:text-gray-600 cursor-not-allowed bg-transparent'
                : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 bg-white dark:bg-gray-800'
            }`}
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            <i className="ri-arrow-left-s-line text-lg" />
          </button>

          <div className="flex items-center gap-1">
            {getPaginationItems(
              table.getState().pagination.pageIndex + 1,
              table.getPageCount()
            ).map((item, key) => (
              <React.Fragment key={key}>
                {item === '...' ? (
                  <span className="px-2 text-gray-400 dark:text-gray-500">⋯</span>
                ) : (
                  <button
                    className={`h-8 min-w-[2rem] px-3 rounded-md border transition-colors text-sm font-medium ${
                      table.getState().pagination.pageIndex === item - 1
                        ? 'bg-gray-900 dark:bg-blue-600 text-white border-gray-900 dark:border-blue-600 hover:bg-gray-800 dark:hover:bg-blue-700'
                        : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 bg-white dark:bg-gray-800'
                    }`}
                    onClick={() => {
                      if (table.getState().pagination.pageIndex !== item - 1) {
                        table.setPageIndex(item - 1);
                      }
                    }}
                  >
                    {item}
                  </button>
                )}
              </React.Fragment>
            ))}
          </div>

          <button
            className={`h-8 w-8 rounded-md border transition-colors ${
              !table.getCanNextPage()
                ? 'border-gray-200 dark:border-gray-700 text-gray-400 dark:text-gray-600 cursor-not-allowed bg-transparent'
                : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 bg-white dark:bg-gray-800'
            }`}
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            <i className="ri-arrow-right-s-line text-lg" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default Table;
